import { API_BASE_URL } from '@/common/config/runtime.ts';
import { addAppBreadcrumb } from '@/common/telemetry/sentry.ts';
import type { QueryClient } from '@tanstack/react-query';

type ResyncEntity = 'work-orders';

type ResyncEvent = {
  id: string;
  type: 'resync';
  entity: ResyncEntity;
  occurredAt: string;
};

const RESYNC_DEBOUNCE_MS = 500;

const queryKeysByEntity: Record<ResyncEntity, readonly unknown[][]> = {
  'work-orders': [
    ['/api/v1/account/work-order'],
    ['/api/v1/account/work-order/reports'],
    ['/api/v1/account/work-order/custom-reports'],
  ],
};

const detailQueryKeyPrefixesByEntity: Record<ResyncEntity, readonly string[]> =
  {
    'work-orders': ['/api/v1/account/work-order/'],
  };

function isDetailQueryKeyForEntity(
  entity: ResyncEntity,
  queryKey: readonly unknown[],
) {
  const [resource] = queryKey;

  return (
    typeof resource === 'string' &&
    detailQueryKeyPrefixesByEntity[entity].some((prefix) =>
      resource.startsWith(prefix),
    )
  );
}

function getRealtimeUrl() {
  return new URL('/api/v1/realtime/resync', API_BASE_URL).toString();
}

function parseResyncEvent(data: string): ResyncEvent | null {
  try {
    const event = JSON.parse(data) as Partial<ResyncEvent>;
    if (event.type !== 'resync' || event.entity !== 'work-orders') {
      return null;
    }
    return event as ResyncEvent;
  } catch {
    return null;
  }
}

export function startResyncEventListener(queryClient: QueryClient) {
  if (typeof window === 'undefined') return () => {};

  const timers = new Map<ResyncEntity, number>();
  const eventSource = new EventSource(getRealtimeUrl(), {
    withCredentials: true,
  });

  eventSource.onopen = () => {
    addAppBreadcrumb({
      category: 'realtime.resync',
      message: 'Opened resync event stream',
      level: 'info',
    });
  };

  eventSource.onerror = () => {
    addAppBreadcrumb({
      category: 'realtime.resync',
      message: 'Resync event stream error',
      level: 'warning',
      data: { readyState: eventSource.readyState },
    });
  };

  eventSource.addEventListener('resync', (message) => {
    const event = parseResyncEvent(message.data);
    if (!event) return;

    window.clearTimeout(timers.get(event.entity));
    timers.set(
      event.entity,
      window.setTimeout(() => {
        for (const queryKey of queryKeysByEntity[event.entity]) {
          void queryClient.invalidateQueries({ queryKey });
        }
        void queryClient.invalidateQueries({
          type: 'all',
          stale: true,
          predicate: (query) =>
            isDetailQueryKeyForEntity(event.entity, query.queryKey),
        });
        timers.delete(event.entity);
      }, RESYNC_DEBOUNCE_MS),
    );

    addAppBreadcrumb({
      category: 'realtime.resync',
      message: 'Received resync event',
      level: 'info',
      data: { entity: event.entity, eventId: event.id },
    });
  });

  return () => {
    for (const timer of timers.values()) {
      window.clearTimeout(timer);
    }
    addAppBreadcrumb({
      category: 'realtime.resync',
      message: 'Closed resync event stream',
      level: 'info',
    });
    eventSource.close();
  };
}
